/* eslint-disable @next/next/no-img-element */
/* eslint-disable indent */
import React, { forwardRef, useContext, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { ACTIONS, EVENTS, STATUS, Step } from "react-joyride";
import { SingleValue } from "react-select";
import { toast } from "react-toastify";
import { captureException } from "@sentry/nextjs";
import { ChevronDown, Coins, HelpCircle, Lock, Map, Mountain, Save, Settings, Skull, Swords, Upload, User, Users, Wand2, X, Zap } from "lucide-react";
import { useRouter } from "next/router";
const CustomJoyride = dynamic(() => import("@/components/atoms/Joyride"), {
  loading: () => null,
  ssr: false
});
import dynamic from "next/dynamic";
import { TypeaheadSelect } from "@/components/atoms/TypeaheadSelect";
import { useTutorial } from "@/components/organisms/TutorialProvider";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { fetchAllLoras, fetchAllPublicLoras, fetchLora, generateImage, generateNpcImage, getArtStyles, getFields, getFieldsForEntity, getModelConfig, getMonsterFields, getThemes, listPresets, pollGenerateImage, pollGenerateNpcImage } from "@/lib/actions";
import { AppContext } from "@/lib/contexts";
import { useGenerateImage, useSavePreset } from "@/lib/hooks";
import { mapCategoryToPresetType, mapPresetTypeToString } from "@/lib/utils";
import { AppReducerActionType, IArtStyle, IFieldOptions, IFreeGenModelDTO, ImageDTO, IModelConfig, IPresetDTO, IPromptOptions, LoraDTO, PresetType, RequestType, UserPremiumType } from "@/types";
import { calculateModelCost, calculateSizeCostForSana, calculateTotalGoldCost } from "@/utils/goldCostUtils";
import { imageFormSteps } from "@/utils/joyride/imageFormSteps";
import { promptingTutorialSteps } from "@/utils/joyride/promptingTutorialSteps";
import { colourScheme } from "@/utils/styling/colours";
import { Label, Span } from "@/utils/styling/typography.styled";
import { STRIPE_CUSTOMER_PORTAL } from "utils/urlConstants";
import ArtStyleSelection from "../ArtStyleSelection";
import EnhancedModelControl from "../EnhancedModelControl";
import LoraSelectionModal from "../LoraSelection";
import ModelSelection from "../ModelSelection";
import QuestionModal from "../QuestionModal";
import { SavePresetModal } from "../SavePresetModal";
import SketchTool, { SketchToolHandle } from "../SketchTool";
type ImageFormProps = {
  loading: boolean;
  setLoading: (val: boolean) => void;
  setError: (val: string) => void;
  onGenerateImages: (images: string | ImageDTO[]) => void;
  presets: IPresetDTO[];
  setPresets: (presets: IPresetDTO[]) => void;
  setIsSavePresetModalOpen: (val: boolean) => void;
  isSavePresetModalOpen: boolean;
};
const fetchDynamicFields = async (category: string): Promise<IFieldOptions | undefined> => {
  if (category === "npcs") {
    try {
      const npcFields = await getFields();
      if (npcFields.fields) {
        delete npcFields.fields.npcFieldsMap["Art Style"];
        return npcFields.fields;
      }
    } catch (error: any) {
      captureException("Failed to fetch fields:", error);
    }
  } else if (category === "monsters") {
    try {
      const monsterFields = await getMonsterFields();
      if (monsterFields.fields) {
        const {
          monsterFieldsMap
        } = monsterFields.fields;
        delete monsterFieldsMap["Art Style"];
        return {
          npcFieldsMap: monsterFieldsMap
        } as IFieldOptions;
      }
    } catch (error: any) {
      captureException("Failed to fetch fields:", error);
    }
  } else if (category === "items") {
    try {
      const itemFields = await getFieldsForEntity("item");
      if (itemFields.fields) {
        const {
          itemFieldsMap
        } = itemFields.fields;
        delete itemFieldsMap["Art Style"];
        return {
          npcFieldsMap: itemFieldsMap
        } as IFieldOptions;
      }
    } catch (error: any) {
      captureException("Failed to fetch fields:", error);
    }
  } else {
    return undefined;
  }
};
type FormValues = {
  [key: string]: string;
};
export interface ImageFormRef {
  handleApplyPreset: (preset: IPresetDTO) => void;
}
const SkeletonForm = () => <div className="p-6 bg-card rounded-lg shadow-lg border border-none space-y-6 md:!w-[968px] md:!h-[1025px]" data-sentry-component="SkeletonForm" data-sentry-source-file="ImageForm.tsx">
        {/* Header */}
        <div className="space-y-2">
            <Skeleton className="h-8 mx-auto" data-sentry-element="Skeleton" data-sentry-source-file="ImageForm.tsx" />
            <Skeleton className="h-4 mx-auto" data-sentry-element="Skeleton" data-sentry-source-file="ImageForm.tsx" />
        </div>

        {/* Quick/Custom Generate Buttons */}
        <div className="grid grid-cols-2 gap-4">
            <Skeleton className="h-12" data-sentry-element="Skeleton" data-sentry-source-file="ImageForm.tsx" />
            <Skeleton className="h-12" data-sentry-element="Skeleton" data-sentry-source-file="ImageForm.tsx" />
        </div>

        {/* Category Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {[...Array(6)].map((_, i) => <Skeleton key={i} className="h-12" />)}
        </div>

        {/* Model Selection */}
        <div className="space-y-4">
            <div className="flex items-center space-x-4">
                <Skeleton className="h-16 w-16" data-sentry-element="Skeleton" data-sentry-source-file="ImageForm.tsx" /> {/* Model image */}
                <div className="space-y-2 flex-1">
                    <Skeleton className="h-6 w-10" data-sentry-element="Skeleton" data-sentry-source-file="ImageForm.tsx" /> {/* Model name */}
                    <Skeleton className="h-4 w-20" data-sentry-element="Skeleton" data-sentry-source-file="ImageForm.tsx" /> {/* Model description */}
                </div>
            </div>
            <Skeleton className="h-10 w-full" data-sentry-element="Skeleton" data-sentry-source-file="ImageForm.tsx" /> {/* Model dropdown */}
        </div>

        {/* Settings Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Image Size */}
            <div className="space-y-2">
                <Skeleton className="h-4 w-24" data-sentry-element="Skeleton" data-sentry-source-file="ImageForm.tsx" />
                <Skeleton className="h-10 w-full" data-sentry-element="Skeleton" data-sentry-source-file="ImageForm.tsx" />
            </div>
            {/* Number of Images */}
            <div className="space-y-2">
                <Skeleton className="h-4 w-24" data-sentry-element="Skeleton" data-sentry-source-file="ImageForm.tsx" />
                <Skeleton className="h-10 w-full" data-sentry-element="Skeleton" data-sentry-source-file="ImageForm.tsx" />
            </div>
        </div>

        {/* Premium Content Switch */}
        <div className="flex items-center space-x-2">
            <Skeleton className="h-6 w-6" data-sentry-element="Skeleton" data-sentry-source-file="ImageForm.tsx" />
            <Skeleton className="h-4 w-32" data-sentry-element="Skeleton" data-sentry-source-file="ImageForm.tsx" />
        </div>

        {/* Art Style and Theme */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
                <Skeleton className="h-4 w-24" data-sentry-element="Skeleton" data-sentry-source-file="ImageForm.tsx" />
                <Skeleton className="h-10 w-full" data-sentry-element="Skeleton" data-sentry-source-file="ImageForm.tsx" />
            </div>
            <div className="space-y-2">
                <Skeleton className="h-4 w-24" data-sentry-element="Skeleton" data-sentry-source-file="ImageForm.tsx" />
                <Skeleton className="h-10 w-full" data-sentry-element="Skeleton" data-sentry-source-file="ImageForm.tsx" />
            </div>
        </div>

        {/* Collapsible Sections */}
        {[...Array(4)].map((_, i) => <div key={i} className="space-y-2">
                <Skeleton className="h-12 w-full" />
            </div>)}

        {/* Footer */}
        <div className="flex justify-between items-center pt-4">
            <Skeleton className="h-4 w-8" data-sentry-element="Skeleton" data-sentry-source-file="ImageForm.tsx" /> {/* Save Preset */}
            <div className="flex items-center space-x-4">
                <Skeleton className="h-4 w-8" data-sentry-element="Skeleton" data-sentry-source-file="ImageForm.tsx" /> {/* Total Gold Cost */}
                <Skeleton className="h-4 w-16" data-sentry-element="Skeleton" data-sentry-source-file="ImageForm.tsx" /> {/* Generate Button */}
            </div>
        </div>
    </div>;
const ImageForm = forwardRef<ImageFormRef, ImageFormProps>(({
  loading,
  setLoading,
  setError,
  onGenerateImages,
  presets,
  setPresets,
  setIsSavePresetModalOpen,
  isSavePresetModalOpen
}, ref) => {
  const router = useRouter();
  const {
    query
  } = router;
  const {
    state,
    dispatch
  } = useContext(AppContext);
  const [category, setCategory] = useState("characters");
  const [formType, setFormType] = useState("detailed");
  const [dynamicFields, setDynamicFields] = useState<IFieldOptions | null>();
  const [formValues, setFormValues] = useState<FormValues>({
    "Art Style": "Realistic",
    Size: "1024x1024",
    "Number of Images": "1"
  });
  const isSubscribed = state.userPremiumLevel !== UserPremiumType.NONE && state.userPremiumLevel !== UserPremiumType.BASIC;
  const [file, setFile] = useState<File | null>(null);
  const [url, setUrl] = useState("");
  const [isHowToModalOpen, setHowToModalOpen] = useState(false);
  const [referenceImage, setReferenceImage] = useState<File | null>(null);
  const [referencePreview, setReferencePreview] = useState<string | null>(null);
  const [customFields, setCustomFields] = useState<{
    [key: string]: boolean;
  }>({});
  const [prompt, setPrompt] = useState("");
  const [removeWatermark, setRemoveWatermark] = useState(false);
  const [isPremium, setIsPremium] = useState<boolean>(false);
  const [useCustomConfig, setUseCustomConfig] = useState(false);
  const [useModel, setUseModel] = useState(false);
  const [config, setConfig] = useState<IFreeGenModelDTO>({
    model: "",
    steps: "",
    cfg: "",
    sampler: ""
  });
  const [modelConfigs, setModelConfigs] = useState<IModelConfig[]>([]);
  const [selectedModel, setSelectedModel] = useState<IModelConfig | null>(null);
  const [artStyles, setArtStyles] = useState<IArtStyle[]>([]);
  const [themes, setThemes] = useState<IPromptOptions[]>([]);
  const [theme, setTheme] = useState<IPromptOptions | null>(null);
  const standardSizes = ["1024x1024", "896x1152", "832x1216", "768x1344", "640x1536", "1536x640", "1344x768", "1216x832", "1152x896"];

  // Define state variables for custom width and height
  const [customWidth, setCustomWidth] = useState(1024);
  const [customHeight, setCustomHeight] = useState(1024);
  const categories = [{
    value: "characters",
    label: "Characters",
    icon: User
  }, {
    value: "npcs",
    label: "NPCs",
    icon: Users
  }, {
    value: "monsters",
    label: "Monsters",
    icon: Skull
  }, {
    value: "items",
    label: "Items",
    icon: Swords
  }, {
    value: "landscapes",
    label: "Landscapes",
    icon: Mountain
  }, {
    value: "battlemaps",
    label: "Battlemaps",
    icon: Map
  }];
  const modelsWithLoras = ["Flux.Pro", "Flux.Dev", "Atomix", "Illustration Ghibli", "Flux.Pro (Ultra)", "Flux.Pro Canny", "Flux.Pro Depth"];
  const premiumSizes = ["1024x1024", "1024x1792", "1792x1024"];
  const [includeArtStyle, setIncludeArtStyle] = useState(false);
  const sketchRef = useRef<SketchToolHandle>(null);
  const validateSize = (width: number, height: number) => {
    if (width < 640 || width > 4096 || height < 640 || height > 4096 || width % 64 !== 0 || height % 64 !== 0) {
      return false;
    }
    return true;
  };
  const [selectedLoras, setSelectedLoras] = useState<LoraDTO[]>([]);
  const [publicLoras, setPublicLoras] = useState<LoraDTO[]>([]);
  const [privateLoras, setPrivateLoras] = useState<LoraDTO[]>([]);
  const [privateLora, setPrivateLora] = useState<boolean>(false);
  const [filteredLoras, setFilteredLoras] = useState<LoraDTO[]>([]);
  const [loadingLoras, setLoadingLoras] = useState(true);
  const [loadingArtStyles, setLoadingArtStyles] = useState(true);
  const [loadingThemes, setLoadingThemes] = useState(true);
  const [loadingModels, setLoadingModels] = useState(true);
  const [loadingPresets, setLoadingPresets] = useState(true);
  const totalGoldCost = useMemo(() => calculateTotalGoldCost(selectedModel?.modelName || null, isPremium, formValues["Size"] || null, false, useCustomConfig ? parseInt(config.steps || "0", 10) : 0, isPremium ? 1 : parseInt(formValues["Number of Images"]) || 1, state.userPremiumLevel, selectedLoras && selectedLoras.length > 0 || false, removeWatermark), [selectedModel?.modelName, isPremium, formValues, useCustomConfig, config.steps, state.userPremiumLevel, selectedLoras, removeWatermark]);
  const {
    tutorialState,
    tutorialStateInitialized,
    startTutorial,
    completeTutorial
  } = useTutorial();
  const [run, setRun] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  useEffect(() => {
    if (!tutorialStateInitialized || !tutorialState) return;
    if (!tutorialState.generateImageForm.completed && !tutorialState.generateImageForm.started && formType === "detailed") {
      startTutorial("generateImageForm");
    }
  }, [tutorialStateInitialized, tutorialState, startTutorial, formType]);
  useEffect(() => {
    if (tutorialState?.generateImageForm.started && !tutorialState.generateImageForm.completed && formType === "detailed") {
      setRun(true);
    }
  }, [tutorialState, formType]);
  const handleJoyrideCallback = (data: {
    status: any;
    index: any;
    action: any;
    type: any;
  }) => {
    const {
      status,
      index,
      action,
      type
    } = data;
    if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status) || action === ACTIONS.CLOSE) {
      completeTutorial("generateImageForm");
      setRun(false);
      setStepIndex(0);
    } else if (type === "step:before") {
      if (index === 4) {
        document.getElementById("character-specific")?.click();
      }
    } else if (type === EVENTS.STEP_AFTER) {
      if (action === ACTIONS.NEXT) {
        setStepIndex(prev => prev + 1);
      } else if (action === ACTIONS.PREV) {
        setStepIndex(prev => prev - 1);
      }
    }
  };
  const [runPromptTutorial, setRunPromptTutorial] = useState(false);
  const [promptStepIndex, setPromptStepIndex] = useState(0);
  useEffect(() => {
    if (!tutorialStateInitialized || !tutorialState) return;
    if (!tutorialState.promptingTutorial?.completed && !tutorialState.promptingTutorial?.started) {
      startTutorial("promptingTutorial");
    }
  }, [tutorialState, startTutorial, tutorialStateInitialized]);
  useEffect(() => {
    if (tutorialState?.promptingTutorial?.started && !tutorialState.promptingTutorial.completed && formType === "simple") {
      setRunPromptTutorial(true);
    }
  }, [formType, tutorialState]);
  const handlePromptJoyrideCallback = (data: {
    action: any;
    index: any;
    type: any;
    status: any;
  }) => {
    const {
      action,
      index,
      type,
      status
    } = data;
    const stepsToSkip = [".negativePrompt"];
    const isStepTargetAvailable = (stepIndex: number) => {
      const step = promptingTutorialSteps[stepIndex] as Step;
      return step && document.querySelector(step.target as string) !== null;
    };
    const getNextStepIndex = (currentIndex: number, direction: "next" | "prev") => {
      let newIndex = direction === "next" ? currentIndex + 1 : currentIndex - 1;

      // Keep checking the next/previous step and skip it if the target is unavailable
      while (stepsToSkip.includes(promptingTutorialSteps[newIndex]?.target as string) && !isStepTargetAvailable(newIndex)) {
        newIndex = direction === "next" ? newIndex + 1 : newIndex - 1;
      }
      return newIndex;
    };
    if (status === "finished" || status === "skipped" || action === ACTIONS.CLOSE) {
      setRunPromptTutorial(false);
      setPromptStepIndex(0);
      completeTutorial("promptingTutorial");
    } else if (type === "step:after") {
      // Check direction and find next or previous step index
      const nextStepIndex = getNextStepIndex(index, action === "next" ? "next" : "prev");
      setPromptStepIndex(nextStepIndex);
    }
  };
  useEffect(() => {
    const fetchLoras = async () => {
      try {
        const publicLoras = await fetchAllPublicLoras();
        const privateLoras = await fetchAllLoras();
        setPublicLoras(publicLoras.loras);
        setPrivateLoras(privateLoras.loras);
        setFilteredLoras(publicLoras.loras.filter(lora => !lora.model || lora.model === "Flux.Dev"));
        setLoadingLoras(false);
      } catch (error) {
        console.error("Error fetching LORAs:", error);
        toast.error("Failed to fetch LORAs. Please try again.");
      }
    };
    fetchLoras();
  }, []);
  const toggleLora = (checked: boolean) => {
    setPrivateLora(checked);
  };
  const onLoraSelected = (loras: LoraDTO[]) => {
    setSelectedLoras(loras);
  };
  const handleDeselectLora = (loraId: string) => {
    setSelectedLoras(prev => prev.filter(lora => lora.id !== loraId));
  };

  // Set initial state from query parameters
  useEffect(() => {
    if (!router.isReady) return; // Wait until router is ready

    const knownParams = ["category", "formType", "contentType", "imageReference", "via", "loraId", "model", "referralCode", "checkout"];
    setCategory(Array.isArray(query.category) ? query.category[0] : query.category || "characters");
    setFormType(Array.isArray(query.formType) ? query.formType[0] : query.formType || "detailed");
    if (query.imageReference) {
      const fetchImage = async (url: string) => {
        try {
          const response = await fetch(url);
          const blob = await response.blob();
          const file = new File([blob], "referenceImage.jpg", {
            type: blob.type
          });
          setReferenceImage(file);
          const reader = new FileReader();
          reader.onloadend = () => {
            setReferencePreview(reader.result as string);
          };
          reader.readAsDataURL(file);
        } catch (error) {
          console.error("Failed to fetch image from URL:", error);
        }
      };
      fetchImage(query.imageReference as string);
    }
    if (query.model) {
      const getModel = async (model: string) => {
        try {
          const response = await getModelConfig();
          if (response.modelConfigs) {
            const defaultModel = response.modelConfigs.find(m => m.modelName === model);
            if (defaultModel) {
              setSelectedModel(defaultModel || response.modelConfigs[0]);
              handleConfigChange({
                model: defaultModel.modelName,
                cfg: defaultModel.defaultCfgScale.toString(),
                steps: defaultModel.defaultSteps.toString(),
                sampler: defaultModel.defaultSampler
              });
            }
          }
        } catch (error) {
          console.error("Failed to fetch model:", error);
        }
      };
      getModel(query.model as string);
    }
    if (query.loraId) {
      const getLora = async (id: string) => {
        try {
          const response = await fetchLora(id);
          if (response.lora) {
            setSelectedLoras([response.lora]);
          }
        } catch (error) {}
      };
      getLora(query.loraId as string);
    }
    const initialFormValues = {
      ...formValues
    };
    Object.keys(query).forEach(key => {
      if (!knownParams.includes(key)) {
        const value = query[key];
        if (Array.isArray(value)) {
          initialFormValues[key] = value[0] || "";
        } else if (typeof value === "string") {
          initialFormValues[key] = value;
        } else {
          initialFormValues[key] = "";
        }
      }
    });
    setFormValues(initialFormValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.isReady]);
  useEffect(() => {
    if (referenceImage && isPremium) {
      toast.error("Premium mode does not currently support image referencing.");
      setReferenceImage(null);
      setReferencePreview(null);
    }
  }, [referenceImage, isPremium]);
  useEffect(() => {
    const fetchArtStyles = async () => {
      try {
        const data = await getArtStyles();
        setArtStyles(data as unknown as IArtStyle[]);
        setLoadingArtStyles(false);
      } catch (err) {
        console.error((err as Error).message);
      }
    };
    fetchArtStyles();
  }, []);
  useEffect(() => {
    const fetchThemes = async () => {
      try {
        const {
          themes
        } = await getThemes();
        setThemes(themes);
        setTheme(themes.filter((theme: IPromptOptions) => theme.fullName === "Epic Fantasy")[0]);
        setLoadingThemes(false);
      } catch (err) {
        console.error((err as Error).message);
      }
    };
    fetchThemes();
  }, []);

  // fetch video model configs
  useEffect(() => {
    const fetchData = async () => {
      try {
        const data = await getModelConfig();
        setModelConfigs(data.modelConfigs);
        if (data.modelConfigs.length > 0) {
          const selectedPresetModel = data.modelConfigs.find(model => model.modelName === "Flux.Dev");
          if (selectedPresetModel) {
            setSelectedModel(selectedPresetModel); // Update selected model
            setConfig({
              model: selectedPresetModel.modelName,
              steps: selectedPresetModel.defaultSteps.toString(),
              cfg: selectedPresetModel.defaultCfgScale.toString(),
              sampler: selectedPresetModel.defaultSampler
            });
            setUseModel(true);
            setLoadingModels(false);
          }
        }
      } catch (err) {
        console.error("Failed to fetch audio model configs:", err);
      }
    };
    fetchData();
  }, []);
  useEffect(() => {
    if (selectedModel?.modelName === "Flux.Pro" || selectedModel?.modelName === "Ideogram 2.0" || selectedModel?.modelName === "Recraft V3" || selectedModel?.modelName === "Flux.Pro (Ultra)" || selectedModel?.modelName === "Flux.Pro (Ultra) Redux" || selectedModel?.modelName === "Luma Photon" || selectedModel?.modelName === "Luma Photon Flash" || isPremium) {
      setFormValues(prevFormValues => ({
        ...prevFormValues,
        "Number of Images": "1"
      }));
    }
  }, [selectedModel, isPremium]);
  useEffect(() => {
    if (selectedModel?.modelName === "Sana") {
      // Set default custom size when Sana is selected
      setCustomWidth(1024);
      setCustomHeight(1024);
      handleSelectChange("Size")({
        value: "1024x1024",
        label: "1024x1024"
      });
    } else if (selectedModel?.modelName === "Flux.Dev" || selectedModel?.modelName === "Atomix" || selectedModel?.modelName === "Illustration Ghibli") {
      if (selectedLoras.length > 0 && (!selectedLoras[0].model || selectedLoras[0].model !== "Flux.Dev")) {
        setSelectedLoras([]);
      }
      if (privateLora) {
        setFilteredLoras(privateLoras.filter(lora => !lora.model || lora.model === "Flux.Dev"));
      } else {
        setFilteredLoras(publicLoras.filter(lora => !lora.model || lora.model === "Flux.Dev"));
      }
    } else if (selectedModel?.modelName === "Flux.Pro" || selectedModel?.modelName === "Flux.Pro Canny" || selectedModel?.modelName === "Flux.Pro Depth" || selectedModel?.modelName === "Flux.Pro (Ultra)") {
      if (selectedLoras.length > 0 && selectedLoras[0]?.model !== "Flux.Pro") {
        setSelectedLoras([]);
      }
      if (privateLora) {
        setFilteredLoras(privateLoras.filter(lora => lora.model === "Flux.Pro"));
      } else {
        setFilteredLoras(publicLoras.filter(lora => lora.model === "Flux.Pro"));
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedModel, privateLora]);
  useEffect(() => {
    if (selectedModel?.modelName === "Sana") {
      // When "Sana" is selected, update the image size
      handleSelectChange("Size")({
        value: `${customWidth}x${customHeight}`,
        label: `${customWidth}x${customHeight}`
      });
    }
  }, [customWidth, customHeight, selectedModel?.modelName]);
  useEffect(() => {
    fetchDynamicFields(category).then(fields => {
      if (fields) {
        setDynamicFields(fields);
      } else {
        setDynamicFields(null);
      }
      setFormValues({
        ...formValues,
        "Art Style": "Realistic",
        Size: "1024x1024",
        "Number of Images": "1"
      });
      if (selectedModel?.modelName === "Sana") {
        setCustomWidth(1024);
        setCustomHeight(1024);
      }
      if (category === "landscapes" || category === "items" || category === "battlemaps") {
        handleInputThemeChange({
          target: {
            value: ""
          }
        } as React.ChangeEvent<HTMLInputElement>);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category]);
  const handleGenerateImage = useGenerateImage({
    requestType: category === "characters" ? null : category === "npcs" ? RequestType.NPC : category === "monsters" ? RequestType.MONSTER : category === "items" ? RequestType.ITEM : category === "landscapes" ? RequestType.LANDSCAPE : category === "battlemaps" ? RequestType.BATTLEMAP : null,
    prompt: formType === "simple" ? prompt : `${theme?.shortName}`,
    file,
    charSheetUrl: url,
    generateImageFunction: category === "characters" ? generateImage : generateNpcImage,
    pollGenerateImageFunction: category === "characters" ? pollGenerateImage : pollGenerateNpcImage,
    imageFile: referenceImage,
    selectedModel,
    manualPrompt: formType === "simple" ? true : false,
    formValues,
    config,
    isPremium,
    useCustomConfig,
    useModel,
    goldCost: totalGoldCost,
    setError,
    setLoading,
    handleSetImages: onGenerateImages,
    includeArtStyle,
    controlNetImage: sketchRef.current?.getImageData() || undefined,
    loras: selectedLoras || [],
    removeWatermark: removeWatermark
  });
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (selectedModel?.imageToImageOnly && !referenceImage) {
      toast.error("Please provide a valid Image Reference to use this model.");
      return;
    }
    if (formType === "detailed" && (category === "items" || category === "landscapes" || category === "battlemaps") && theme?.fullName === "") {
      toast.error(`Please provide a ${category} Prompt.`);
      return;
    }
    const [width, height] = formValues["Size"].split("x").map(Number);
    if (!validateSize(width, height)) {
      alert("Width and Height must be between 640 and 4096, in increments of 64.");
      return;
    }
    console.log("Sketch Ref Data:", sketchRef.current?.getImageData());
    if (sketchRef?.current?.getSketchFidelity()) {
      setFormValues(prev => ({
        ...prev,
        ["Process Shading"]: sketchRef.current?.getShading().toString() || "false",
        ["ControlNet Weight"]: sketchRef.current?.getSketchFidelity().toString() || "0.5"
      }));
    }
    handleGenerateImage(e);
  };
  useEffect(() => {
    if (state.gold === 0) {
      setIsPremium(false);
      setFormValues(prev => ({
        ...prev,
        Description: ""
      }));
      const selectedPresetModel = modelConfigs.find(model => model.modelName === "Flux.Dev");
      if (selectedPresetModel) {
        setSelectedModel(selectedPresetModel); // Update selected model
        setConfig({
          model: selectedPresetModel.modelName,
          steps: selectedPresetModel.defaultSteps.toString(),
          cfg: selectedPresetModel.defaultCfgScale.toString(),
          sampler: selectedPresetModel.defaultSampler
        });
        setUseModel(true);
      }
    }
  }, [state.gold, modelConfigs]);
  useEffect(() => {
    const fetchPresets = async (category: string) => {
      try {
        if (state.isLoggedIn) {
          const response = await listPresets(mapCategoryToPresetType(category));
          if (Array.isArray(response)) {
            setPresets(response);
            setLoadingPresets(false);
          } else {
            console.error("Error fetching presets:", response.error);
          }
        } else {
          setPresets([]);
          setLoadingPresets(false);
        }
      } catch (error) {
        console.error("Error fetching presets:", error);
      }
    };
    fetchPresets(category);
  }, [category, setPresets, state.isLoggedIn]);
  const handlePremiumToggle = (checked: boolean) => {
    setIsPremium(checked);
    if (!checked) {
      setFormValues(prev => ({
        ...prev,
        Size: "1024x1024"
      }));
      handleInputChange("Size")({
        target: {
          value: "1024x1024"
        }
      } as React.ChangeEvent<HTMLInputElement>);
    }
    // If the toggle is being turned off and the user's premium level is NONE, reset the description
    if (!checked && state.userPremiumLevel === UserPremiumType.NONE) {
      handleInputChange("Description")({
        target: {
          value: ""
        }
      } as React.ChangeEvent<HTMLInputElement>);
    }
  };
  const toggleCustomField = (field: string) => {
    setCustomFields(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };
  const findTheme = useMemo(() => {
    return (p: string) => themes.find(prompt => prompt.shortName === p);
  }, [themes]);
  const {
    handleSave
  } = useSavePreset(presets, setPresets, setIsSavePresetModalOpen, formType === "characters" ? PresetType.CHARACTER : formType === "npcs" ? PresetType.NPC : formType === "monsters" ? PresetType.MONSTER : formType === "items" ? PresetType.ITEM : formType === "landscapes" ? PresetType.LANDSCAPE : formType === "battlemaps" ? PresetType.BATTLEMAP : PresetType.CHARACTER, {
    shortName: formType === "simple" ? prompt : theme?.shortName || ""
  }, formValues, url, file ?? undefined, isPremium, useCustomConfig, useModel, config, formType === "simple");
  interface Option {
    value: string;
    label: string;
    isDisabled?: boolean;
  }
  const handleThemeChange = (selectedOption: SingleValue<Option>) => {
    if (selectedOption?.isDisabled) {
      return;
    }
    const selectedPrompt = findTheme(selectedOption?.value ?? "");
    if (!selectedPrompt) {
      setTheme({
        shortName: selectedOption?.value as string,
        fullName: selectedOption?.value as string,
        premium: false
      });
    } else {
      setTheme(selectedPrompt);
    }
    if (selectedPrompt?.premium && state.userPremiumLevel === UserPremiumType.NONE) {
      setIsPremium(true);
    }
  };
  const handleInputThemeChange = (event: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLTextAreaElement>) => {
    const {
      value
    } = event.target;
    setTheme({
      shortName: value,
      fullName: value,
      premium: false
    });
  };
  const handleApplyPreset = (preset: IPresetDTO) => {
    setFormValues(preset.requestFields);
    setFormValues(prevFormValues => ({
      ...prevFormValues,
      "Art Style": preset.artStyle
    }));
    const existingTheme = findTheme(preset.prompt);
    if (!existingTheme) {
      setTheme({
        shortName: preset.prompt,
        fullName: preset.prompt,
        premium: false
      });
    } else {
      setTheme(existingTheme);
    }
    setUrl(preset.fileUrl || "");
    setFile(null);
    setIsPremium(preset.premium);
    const simpleForm = preset.simpleForm ? "simple" : "detailed";
    setFormType(simpleForm);
    setCategory(mapPresetTypeToString(preset.presetType));
    if (preset.freeGenModelDTO) {
      setConfig(preset.freeGenModelDTO || {
        model: selectedModel?.modelName || "Flux.Dev",
        steps: selectedModel?.defaultSteps.toString() || "28",
        cfg: selectedModel?.defaultCfgScale.toString() || "3.5",
        sampler: selectedModel?.defaultSampler || "DPM++ 2M Karras"
      });
    }
    const conf = preset.freeGenModelDTO ? true : false;
    setUseCustomConfig(preset.isCustomConfig);
    setUseModel(conf);
    const selectedPresetModel = modelConfigs.find(model => model.modelName === preset.freeGenModelDTO?.model);
    if (selectedPresetModel) {
      setSelectedModel(selectedPresetModel); // Update selected model
    }
    toast.success(`Preset "${preset.name}" applied successfully!`, {
      position: "bottom-center"
    });
  };

  // Expose handleApplyPreset to the parent via ref
  useImperativeHandle(ref, () => ({
    handleApplyPreset
  }));
  const renderCategorySelection = () => <div className="flex flex-col space-y-4 mb-4" data-sentry-component="renderCategorySelection" data-sentry-source-file="ImageForm.tsx">
                <div className="md:hidden">
                    {/* Renders an accordion on small screens */}
                    <Accordion type="single" collapsible data-sentry-element="Accordion" data-sentry-source-file="ImageForm.tsx">
                        <AccordionItem id="category" value="category" data-sentry-element="AccordionItem" data-sentry-source-file="ImageForm.tsx">
                            <AccordionTrigger data-sentry-element="AccordionTrigger" data-sentry-source-file="ImageForm.tsx">
                                <Label htmlFor="category" data-sentry-element="Label" data-sentry-source-file="ImageForm.tsx">Category</Label>
                            </AccordionTrigger>
                            <AccordionContent data-sentry-element="AccordionContent" data-sentry-source-file="ImageForm.tsx">
                                <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                                    {categories.map(cat => <Button key={cat.value} className={`flex flex-row items-center gap-2 h-10 px-3 rounded-lg ${category === cat.value ? "!bg-[#4F80CC] !text-[#FFFFFF] hover:!bg-[#3D6EB8]" : "!bg-[#2C2E52] !text-[#E8EAF0] hover:!bg-[#3D4066]"} border border-slate-600/20 transition-colors duration-200`} onClick={e => {
                e.preventDefault();
                e.stopPropagation();
                setCategory(cat.value);
              }}>
                                            <cat.icon style={{
                  width: "1rem",
                  height: "1rem"
                }} />
                                            <Label>{cat.label}</Label>
                                        </Button>)}
                                </div>
                            </AccordionContent>
                        </AccordionItem>
                    </Accordion>
                </div>

                <div className="hidden md:block">
                    <Label htmlFor="category" data-sentry-element="Label" data-sentry-source-file="ImageForm.tsx">Category</Label>
                    {/* Renders a grid of categories on larger screens */}
                    <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                        {categories.map(cat => <Button key={cat.value} className={`flex flex-row items-center gap-2 h-10 px-3 rounded-lg ${category === cat.value ? "!bg-[#4F80CC] !text-[#FFFFFF] hover:!bg-[#3D6EB8]" : "!bg-[#2C2E52] !text-[#E8EAF0] hover:!bg-[#3D4066]"} border border-slate-600/20 transition-colors duration-200`} onClick={e => {
          e.preventDefault();
          e.stopPropagation();
          setCategory(cat.value);
        }}>
                                <cat.icon style={{
            width: "1rem",
            height: "1rem"
          }} />
                                <Label>{cat.label}</Label>
                            </Button>)}
                    </div>
                </div>
            </div>;
  const renderTransformableField = (field: string, options: string[]) => {
    const isTheme = field === "Theme";
    const isCustomField = customFields[field];
    const isArtStyle = field === "Art Style";
    return <div className="flex flex-col flex-start space-y-2 mb-2 w-full" key={field} data-sentry-component="renderTransformableField" data-sentry-source-file="ImageForm.tsx">
                    <div className="flex items-center space-between">
                        <div className="flex items-center space-x-2">
                            <Label htmlFor={field} className={isTheme ? "joyride-theme" : ""} data-sentry-element="Label" data-sentry-source-file="ImageForm.tsx">
                                {field}
                            </Label>

                            <Popover data-sentry-element="Popover" data-sentry-source-file="ImageForm.tsx">
                                <PopoverTrigger asChild data-sentry-element="PopoverTrigger" data-sentry-source-file="ImageForm.tsx">
                                    <Button variant="ghost" className="text-gray-300 hover:text-white !px-0 !py-0 hover:bg-gray-800 !rounded-full transition-all duration-200 md:!flex-initial" aria-label="More options" data-sentry-element="Button" data-sentry-source-file="ImageForm.tsx">
                                        <HelpCircle className="h-4 w-4 text-[#7AA8E3] cursor-help" data-sentry-element="HelpCircle" data-sentry-source-file="ImageForm.tsx" />
                                    </Button>
                                </PopoverTrigger>
                                <PopoverContent className="!rounded-xl border !border-slate-200/20 !bg-slate-950 p-4" data-sentry-element="PopoverContent" data-sentry-source-file="ImageForm.tsx">
                                    <p>
                                        Select or enter a custom{" "}
                                        {field.toLowerCase()} for your{" "}
                                        {category}
                                    </p>
                                </PopoverContent>
                            </Popover>
                        </div>

                        <Button type="button" size="sm" variant="ghost" onClick={() => toggleCustomField(field)} data-sentry-element="Button" data-sentry-source-file="ImageForm.tsx">
                            {customFields[field] ? "Select from List" : "Enter Free Text"}
                        </Button>
                    </div>
                    {isCustomField ? <Input id={field} value={field === "Theme" ? theme?.fullName : formValues[field] || ""} onChange={field === "Theme" ? e => {
        handleInputThemeChange(e);
      } : handleInputChange(field)} placeholder={`Enter custom ${field.toLowerCase()}`} /> : isTheme ? <TypeaheadSelect name="prompt" id="prompt" placeholder="Type" required aria-required value={theme ? {
        value: theme.shortName,
        label: theme.fullName
      } : null} onChange={handleThemeChange} options={themes.map(t => ({
        value: t.shortName,
        label: t.fullName
      }))} isDisabled={loading} /> : isArtStyle ? <ArtStyleSelection artStyles={artStyles} onStyleChange={handleArtStyleChange} /> : <TypeaheadSelect id={field} name={field} value={formValues[field] ? {
        value: formValues[field],
        label: formValues[field]
      } : null} onChange={handleSelectChange(field)} options={options.map(option => ({
        value: option,
        label: option
      }))} placeholder={`Select ${field}`} isClearable={field !== "Art Style"} />}
                </div>;
  };
  const renderCommonFields = () => <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-center" data-sentry-component="renderCommonFields" data-sentry-source-file="ImageForm.tsx">
                {!isPremium && <>
                        <div className="mb-4 flex flex-col gap-4 w-auto joyride-model-selection">
                            {loadingModels ? <div className="w-auto h-auto md:!w-[476px] md:!h-[135px] space-y-4">
                                    <Skeleton className="h-4 w-full" />{" "}
                                    <div className="flex items-center space-x-4 border border-slate-600/40 p-4 rounded-lg animate-pulse">
                                        <Skeleton className="h-16 w-16" />{" "}
                                        {/* Model image */}
                                        <div className="space-y-2 flex-1">
                                            <Skeleton className="h-4 w-auto" />{" "}
                                            {/* Model name */}
                                            <Skeleton className="h-6 w-auto" />{" "}
                                            {/* Model description */}
                                        </div>
                                    </div>
                                    {/* Model dropdown */}
                                </div> : <ModelSelection modelConfigs={modelConfigs} onConfigChange={handleConfigChange} selectedModel={selectedModel} setSelectedModel={setSelectedModel} />}
                            <span className="text-[#F0B853] text-xs">
                                Cost:{" "}
                                {calculateModelCost(selectedModel?.modelName, state.userPremiumLevel)}{" "}
                                gold
                            </span>
                        </div>
                        <div className="mb-4 flex flex-col gap-4 w-auto">
                            <EnhancedModelControl config={config} onConfigChange={handleConfigChange} useCustomConfig={useCustomConfig} setUseCustomConfig={setUseCustomConfig} />
                        </div>
                    </>}
                <div className="mb-4 flex flex-col gap-4 w-auto">
                    <div className="flex items-center space-between">
                        <div className="flex items-center space-x-2">
                            <Label htmlFor="imageSize" className="joyride-image-size" data-sentry-element="Label" data-sentry-source-file="ImageForm.tsx">
                                Image Size
                            </Label>
                            <Popover data-sentry-element="Popover" data-sentry-source-file="ImageForm.tsx">
                                <PopoverTrigger asChild data-sentry-element="PopoverTrigger" data-sentry-source-file="ImageForm.tsx">
                                    <Button variant="ghost" className="text-gray-300 hover:text-white !px-0 !py-0 hover:bg-gray-800 !rounded-full transition-all duration-200 md:!flex-initial" aria-label="More options" data-sentry-element="Button" data-sentry-source-file="ImageForm.tsx">
                                        <HelpCircle className="h-4 w-4 text-[#7AA8E3] cursor-help" data-sentry-element="HelpCircle" data-sentry-source-file="ImageForm.tsx" />
                                    </Button>
                                </PopoverTrigger>
                                <PopoverContent className="!rounded-xl border !border-slate-200/20 !bg-slate-950 p-4" data-sentry-element="PopoverContent" data-sentry-source-file="ImageForm.tsx">
                                    <p>
                                        Select the dimensions of your generated
                                        image
                                    </p>
                                </PopoverContent>
                            </Popover>
                        </div>
                        {(isPremium || selectedModel?.modelName === "Sana") && formValues["Size"] !== "1024x1024" && <span className="text-[#F0B853] text-xs">
                                    Cost: x
                                    {calculateSizeCostForSana(formValues["Size"], isPremium)}
                                </span>}
                    </div>
                    {selectedModel?.modelName === "Sana" ? <div className="space-y-4">
                            <div className="flex space-x-4">
                                <div className="flex-1">
                                    <Label htmlFor="custom-width">
                                        Width: {customWidth}px
                                    </Label>
                                    <Input id="custom-width" type="range" min={640} max={4096} step={64} value={customWidth} onChange={e => {
              const width = parseInt(e.target.value, 10);
              setCustomWidth(width);
              handleSelectChange("Size")({
                value: `${width}x${customHeight}`,
                label: `${width}x${customHeight}`
              });
            }} />
                                </div>
                                <div className="flex-1">
                                    <Label htmlFor="custom-height">
                                        Height: {customHeight}px
                                    </Label>
                                    <Input id="custom-height" type="range" min={640} max={4096} step={64} value={customHeight} onChange={e => {
              const height = parseInt(e.target.value, 10);
              setCustomHeight(height);
              handleSelectChange("Size")({
                value: `${customWidth}x${height}`,
                label: `${customWidth}x${height}`
              });
            }} />
                                </div>
                            </div>
                        </div> :
      // Render existing select dropdown for other models
      <Select value={formValues["Size"]} onValueChange={value => handleSelectChange("Size")({
        value,
        label: value
      })}>
                            <SelectTrigger className="select-trigger">
                                <SelectValue placeholder="Select Image Size" className="joyride-image-size" />
                            </SelectTrigger>
                            <SelectContent className="select-content">
                                {isPremium ? premiumSizes.map(size => <SelectItem key={size} value={size}>
                                              {size}
                                          </SelectItem>) : standardSizes.map(size => <SelectItem key={size} value={size}>
                                              {size}
                                          </SelectItem>)}
                            </SelectContent>
                        </Select>}
                </div>

                <div className="mb-4 flex flex-col gap-4 w-auto">
                    <div className="flex items-center space-between">
                        <div className="flex items-center space-x-2">
                            <Label htmlFor="numberOfImages" className="joyride-number-of-images" data-sentry-element="Label" data-sentry-source-file="ImageForm.tsx">
                                Number of Images
                            </Label>
                            <Popover data-sentry-element="Popover" data-sentry-source-file="ImageForm.tsx">
                                <PopoverTrigger asChild data-sentry-element="PopoverTrigger" data-sentry-source-file="ImageForm.tsx">
                                    <Button variant="ghost" className="text-gray-300 hover:text-white !px-0 !py-0 hover:bg-gray-800 !rounded-full transition-all duration-200 md:!flex-initial" aria-label="More options" data-sentry-element="Button" data-sentry-source-file="ImageForm.tsx">
                                        <HelpCircle className="h-4 w-4 text-[#7AA8E3] cursor-help" data-sentry-element="HelpCircle" data-sentry-source-file="ImageForm.tsx" />
                                    </Button>
                                </PopoverTrigger>
                                <PopoverContent className="!rounded-xl border !border-slate-200/20 !bg-slate-950 p-4" data-sentry-element="PopoverContent" data-sentry-source-file="ImageForm.tsx">
                                    <p>Choose how many images to generate</p>
                                </PopoverContent>
                            </Popover>
                        </div>
                        <span className="text-[#F0B853] text-xs">
                            Cost: x{formValues["Number of Images"] || 1}
                        </span>
                    </div>
                    <Select value={formValues["Number of Images"]} onValueChange={value => handleSelectChange("Number of Images")({
        value,
        label: value
      })} data-sentry-element="Select" data-sentry-source-file="ImageForm.tsx">
                        <SelectTrigger className="select-trigger" data-sentry-element="SelectTrigger" data-sentry-source-file="ImageForm.tsx">
                            <SelectValue placeholder="Number of Images" data-sentry-element="SelectValue" data-sentry-source-file="ImageForm.tsx" />
                        </SelectTrigger>
                        <SelectContent className="select-content" data-sentry-element="SelectContent" data-sentry-source-file="ImageForm.tsx">
                            {selectedModel?.modelName === "Flux.Pro" || selectedModel?.modelName === "Flux.Pro Redux" || selectedModel?.modelName === "Flux.Pro Fill" || selectedModel?.modelName === "Flux.Pro Canny" || selectedModel?.modelName === "Flux.Pro Depth" || selectedModel?.modelName === "Flux.Pro (Ultra) Redux" || selectedModel?.modelName === "Flux.Pro (Ultra)" || selectedModel?.modelName === "Ideogram 2.0" || selectedModel?.modelName === "Recraft V3" || selectedModel?.modelName === "Luma Photon" || selectedModel?.modelName === "Luma Photon Flash" || isPremium ? <SelectItem value="1">1</SelectItem> : <>
                                    <SelectItem value="1">1</SelectItem>
                                    <SelectItem value="2">2</SelectItem>
                                    <SelectItem value="3">3</SelectItem>
                                    <SelectItem value="4">4</SelectItem>
                                </>}
                        </SelectContent>
                    </Select>
                </div>

                <div style={{
      display: "flex",
      alignItems: "center",
      gap: "0.5rem"
    }}>
                    <Switch id="premium" checked={isPremium} onCheckedChange={handlePremiumToggle} className="joyride-premium-toggle" data-sentry-element="Switch" data-sentry-source-file="ImageForm.tsx" />
                    <Span data-sentry-element="Span" data-sentry-source-file="ImageForm.tsx">Use Premium Content</Span>
                </div>
                {(selectedModel?.modelName === "Flux.Pro (Ultra)" || selectedModel?.modelName === "Flux.Pro (Ultra) Redux") && <div style={{
      display: "flex",
      alignItems: "center",
      gap: "0.5rem"
    }}>
                        <Switch id="useRaw" checked={formValues["Raw"] === "true"} onCheckedChange={checked => handleArtStyleChange("Raw", checked.toString())} />
                        <div className="flex items-center space-x-2">
                            <Span>Natural Mode</Span>

                            <Popover>
                                <PopoverTrigger asChild>
                                    <Button variant="ghost" className="text-gray-300 hover:text-white !px-0 !py-0 hover:bg-gray-800 !rounded-full transition-all duration-200 md:!flex-initial" aria-label="More options">
                                        <HelpCircle className="h-4 w-4 text-[#7AA8E3] cursor-help" />
                                    </Button>
                                </PopoverTrigger>
                                <PopoverContent className="!rounded-xl border !border-slate-200/20 !bg-slate-950 p-4">
                                    <p>
                                        Generate less processed, more
                                        natural-looking images.
                                    </p>
                                </PopoverContent>
                            </Popover>
                        </div>
                    </div>}
            </div>;
  const handleInputChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLTextAreaElement>) => {
    setFormValues(prev => ({
      ...prev,
      [field]: e.target.value
    }));
  };
  const handleArtStyleChange = (field: string, value: string) => {
    if (value) {
      setFormValues(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };
  const handleConfigChange = (newConfig: Partial<IFreeGenModelDTO>) => {
    setUseModel(true);
    setConfig(prevConfig => ({
      ...prevConfig,
      ...newConfig
    }));
  };
  const modelSupportsImageToImage = (model: IModelConfig | null) => {
    return !isPremium && model?.imageToImage;
  };
  const modelSupportsControlNet = (model: IModelConfig | null) => {
    return !isPremium && model?.hasControlNet;
  };
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile && selectedFile.type === "application/pdf") {
      setFile(selectedFile);
    } else {
      setFile(null);
      toast.error("Please select a valid PDF file.");
    }
  };
  const handleUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newUrl = e.target.value;
    setUrl(newUrl);
    if (newUrl.includes("dndbeyond.com") && !newUrl.endsWith(".pdf")) {
      setHowToModalOpen(true);
    }
  };
  const handleSelectChange = (field: string) => (option: {
    value: string;
    label: string;
  } | null) => {
    setFormValues(prev => ({
      ...prev,
      [field]: option ? option.value : ""
    }));
  };
  const handleReferenceUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && file.type.startsWith("image/")) {
      setReferenceImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setReferencePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    } else {
      toast.error("Please select a valid image file.");
    }
  };
  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };
  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    const file = e.dataTransfer.files[0];
    if (file && file.type.startsWith("image/")) {
      setReferenceImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setReferencePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    } else {
      toast.error("Please select a valid image file.");
    }
  };
  const renderReferenceUpload = () => <Accordion type="multiple" data-sentry-element="Accordion" data-sentry-component="renderReferenceUpload" data-sentry-source-file="ImageForm.tsx">
                <AccordionItem value="reference-upload" className="joyride-image-upload" data-sentry-element="AccordionItem" data-sentry-source-file="ImageForm.tsx">
                    <AccordionTrigger className="text-white" data-sentry-element="AccordionTrigger" data-sentry-source-file="ImageForm.tsx">
                        Upload Reference Image (Optional)
                    </AccordionTrigger>
                    <AccordionContent data-sentry-element="AccordionContent" data-sentry-source-file="ImageForm.tsx">
                        <div className="flex flex-col space-y-4 pt-4">
                            <div className="flex flex-row gap-4">
                                <Label htmlFor="reference" data-sentry-element="Label" data-sentry-source-file="ImageForm.tsx">
                                    Reference Image (Optional)
                                </Label>
                                <Popover data-sentry-element="Popover" data-sentry-source-file="ImageForm.tsx">
                                    <PopoverTrigger asChild data-sentry-element="PopoverTrigger" data-sentry-source-file="ImageForm.tsx">
                                        <Button variant="ghost" className="text-gray-300 hover:text-white !px-0 !py-0 hover:bg-gray-800 !rounded-full transition-all duration-200 md:!flex-initial" aria-label="More options" data-sentry-element="Button" data-sentry-source-file="ImageForm.tsx">
                                            <HelpCircle className="h-4 w-4 text-[#7AA8E3] cursor-help" data-sentry-element="HelpCircle" data-sentry-source-file="ImageForm.tsx" />
                                        </Button>
                                    </PopoverTrigger>
                                    <PopoverContent className="!rounded-xl border !border-slate-200/20 !bg-slate-950 p-4" data-sentry-element="PopoverContent" data-sentry-source-file="ImageForm.tsx">
                                        <p>
                                            Upload an image to guide the AI in
                                            matching your desired style, colors,
                                            and composition.
                                        </p>
                                        <br />
                                        <p>
                                            This ensures the generated artwork
                                            aligns closely with your creative
                                            vision
                                        </p>
                                    </PopoverContent>
                                </Popover>
                            </div>
                            <div className="mb-4 flex flex-col gap-4 w-auto">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center space-x-2">
                                        <Label htmlFor="exportFps" data-sentry-element="Label" data-sentry-source-file="ImageForm.tsx">
                                            Reference Image Creativity:
                                        </Label>
                                        <Popover data-sentry-element="Popover" data-sentry-source-file="ImageForm.tsx">
                                            <PopoverTrigger asChild data-sentry-element="PopoverTrigger" data-sentry-source-file="ImageForm.tsx">
                                                <Button variant="ghost" className="text-gray-300 hover:text-white !px-0 !py-0 hover:bg-gray-800 !rounded-full transition-all duration-200 md:!flex-initial" aria-label="More options" data-sentry-element="Button" data-sentry-source-file="ImageForm.tsx">
                                                    <HelpCircle className="h-4 w-4 text-[#7AA8E3] cursor-help" data-sentry-element="HelpCircle" data-sentry-source-file="ImageForm.tsx" />
                                                </Button>
                                            </PopoverTrigger>
                                            <PopoverContent className="!rounded-xl border !border-slate-200/20 !bg-slate-950 p-4" data-sentry-element="PopoverContent" data-sentry-source-file="ImageForm.tsx">
                                                <p>
                                                    The amount of creativity the
                                                    AI is allowed over the
                                                    reference image.
                                                </p>
                                                <br />
                                                <p>
                                                    A lower score means closer
                                                    adherence to the reference,
                                                    a higher score allows for
                                                    more creativity and
                                                    deviation.
                                                </p>
                                            </PopoverContent>
                                        </Popover>
                                    </div>
                                    <span className="text-sm font-medium">
                                        {formValues["Reference Image Strength"] ?? 0.8}
                                    </span>
                                </div>
                                <Slider value={[parseFloat(formValues["Reference Image Strength"] ?? "0.8")]} onValueChange={value => handleArtStyleChange("Reference Image Strength", value[0].toString())} min={0} max={1} step={0.01} defaultValue={[0.8]} className="w-full h-4 bg-slate-900 rounded-full" data-sentry-element="Slider" data-sentry-source-file="ImageForm.tsx" />
                            </div>
                            <div className="p-4 pointer rounded-lg border-[#4F80CC] border !border-dashed text-center transition-bg hover:bg-[#2C2E52] duration-200" onDragOver={handleDragOver} onDrop={handleDrop} onClick={() => document.getElementById("reference-upload")?.click()}>
                                <Input className="hidden" id="reference-upload" type="file" accept="image/*" onChange={handleReferenceUpload} data-sentry-element="Input" data-sentry-source-file="ImageForm.tsx" />
                                <p className="text-white">
                                    Drag and drop an image here, or click to
                                    select a file
                                </p>
                            </div>
                            {referencePreview && <div className="relative w-32 h-32 mt-2">
                                    <img className="w-full h-full object-cover rounded-md" src={referencePreview} alt="Reference" />
                                    <Button className="absolute top-0 right-0 !bg-[#EB4D4B] text-white !rounded-full hover:!bg-[#D84341]" type="button" size="icon" onClick={() => {
              setReferenceImage(null);
              setReferencePreview(null);
            }}>
                                        <X size={16} />
                                    </Button>
                                </div>}
                        </div>
                    </AccordionContent>
                </AccordionItem>
            </Accordion>;
  const renderSketchTool = () => <Accordion type="multiple" key="sketch-tool" data-sentry-element="Accordion" data-sentry-component="renderSketchTool" data-sentry-source-file="ImageForm.tsx">
                <AccordionItem value="sketch-tool-upload" className="joyride-sketch-tool-upload" data-sentry-element="AccordionItem" data-sentry-source-file="ImageForm.tsx">
                    <AccordionTrigger className="text-white" data-sentry-element="AccordionTrigger" data-sentry-source-file="ImageForm.tsx">
                        Draw Sketch on Canvas (Optional)
                    </AccordionTrigger>
                    <AccordionContent data-sentry-element="AccordionContent" data-sentry-source-file="ImageForm.tsx">
                        {state.isLoggedIn && state.userPremiumLevel === UserPremiumType.NONE ? <Card className="w-full max-w-md mx-auto bg-gray-900 text-gray-100">
                                <CardHeader>
                                    <CardTitle className="text-2xl font-bold flex items-center gap-2">
                                        <Lock className="w-6 h-6" />
                                        Subscriber Only Feature
                                    </CardTitle>
                                    <CardDescription className="text-gray-400">
                                        Unlock the Sketch Tool by becoming a
                                        subscriber
                                    </CardDescription>
                                </CardHeader>
                                <CardContent>
                                    <p className="text-sm mb-4">
                                        The Sketch Tool is an advanced feature
                                        that allows you to create custom
                                        sketches for image generation. Subscribe
                                        now to access this and other premium
                                        features!
                                    </p>
                                    <ul className="list-disc pl-4">
                                        <li>
                                            Create detailed sketches for image
                                            generation
                                        </li>
                                        <li>
                                            Use various drawing tools and
                                            controls
                                        </li>
                                        <li>Enhance your creative process</li>
                                        <li>Access to all premium features</li>
                                    </ul>
                                </CardContent>
                                <CardFooter>
                                    <Button className="w-full bg-emerald-500 hover:bg-chart-2 text-white" size="lg" onClick={e => {
              e.preventDefault();
              e.stopPropagation();
              dispatch({
                type: AppReducerActionType.SHOW_SUBSCRIPTION_TABLE,
                payload: {
                  ...state,
                  showSubscriptionTable: true
                }
              });
            }}>
                                        Subscribe Now
                                    </Button>
                                </CardFooter>
                            </Card> : <div className="flex flex-col space-y-4 pt-4">
                                <div className="flex flex-row gap-4">
                                    <Label htmlFor="sketch-tool-upload">
                                        Draw Sketch on Canvas (Optional)
                                    </Label>
                                    <Popover>
                                        <PopoverTrigger asChild>
                                            <Button variant="ghost" className="text-gray-300 hover:text-white !px-0 !py-0 hover:bg-gray-800 !rounded-full transition-all duration-200 md:!flex-initial" aria-label="More options">
                                                <HelpCircle className="h-4 w-4 text-[#7AA8E3] cursor-help" />
                                            </Button>
                                        </PopoverTrigger>
                                        <PopoverContent className="!rounded-xl border !border-slate-200/20 !bg-slate-950 p-4">
                                            <p>
                                                Draw a sketch of what you&apos;d
                                                like your image to look like.
                                            </p>
                                            <br />
                                            <p>
                                                The output image will follow the
                                                sketch as closely as possible in
                                                terms of poses, colour, and
                                                composition.
                                            </p>
                                        </PopoverContent>
                                    </Popover>
                                </div>
                                <SketchTool ref={sketchRef} />
                            </div>}
                    </AccordionContent>
                </AccordionItem>
            </Accordion>;
  const renderDetailedForm = () => <div style={{
    display: "flex",
    flexDirection: "column",
    gap: "0.5rem"
  }} data-sentry-component="renderDetailedForm" data-sentry-source-file="ImageForm.tsx">
                {renderCommonFields()}
                {<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {loadingArtStyles ? <div className="space-y-2">
                                <Skeleton className="h-4 w-24" />
                                <Skeleton className="h-20 w-full" />
                            </div> : renderTransformableField("Art Style", [])}
                        {loadingThemes ? <div className="flex flex-col flex-start space-y-2 !mb-2 !w-full">
                                <Skeleton className="h-9 w-full" />
                                <Skeleton className="h-10 w-full" />
                            </div> : (category === "characters" || category === "npcs" || category === "monsters") && renderTransformableField("Theme", themes.map(t => t.fullName))}
                    </div>}

                {loadingLoras ? <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Image Size */}
                        <div className="space-y-2">
                            <Skeleton className="h-9 w-full" />
                        </div>
                        {/* Number of Images */}
                        <div className="space-y-2">
                            <Skeleton className="h-11 w-full" />
                        </div>
                    </div> : selectedModel && modelsWithLoras.includes(selectedModel.modelName) && !isPremium ? <>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <LoraSelectionModal loras={filteredLoras} onLoraSelected={onLoraSelected} selectedLoras={selectedLoras} handleToggleChange={toggleLora} />
                            <div style={{
          display: "flex",
          alignItems: "center",
          gap: "0.5rem"
        }}>
                                <Switch id="disableArtStyleLora" checked={formValues["Disable Art Style"] === "true"} onCheckedChange={checked => handleArtStyleChange("Disable Art Style", checked.toString())} className="" />
                                <Span>Disable Art Style Lora</Span>
                                <Popover>
                                    <PopoverTrigger asChild>
                                        <Button variant="ghost" className="text-gray-300 hover:text-white !px-0 !py-0 hover:bg-gray-800 !rounded-full transition-all duration-200 md:!flex-initial" aria-label="More options">
                                            <HelpCircle className="h-4 w-4 text-[#7AA8E3] cursor-help" />
                                        </Button>
                                    </PopoverTrigger>
                                    <PopoverContent className="!rounded-xl border !border-slate-200/20 !bg-slate-950 p-4">
                                        <p className="mb-2">
                                            Art Styles include specific prompt
                                            wording and a custom-trained Lora to
                                            recreate the style.
                                        </p>
                                        <p className="mb-2">
                                            Toggle this option to:
                                        </p>
                                        <ul className="list-disc pl-4">
                                            <li>
                                                Disable the Art Style Lora (Not
                                                your selected Loras)
                                            </li>
                                            <li>
                                                And Retain the Art Style Prompt
                                                Wording
                                            </li>
                                            <li>
                                                Avoid conflicts between your
                                                selected Loras and the Art
                                                Style.
                                            </li>
                                        </ul>
                                    </PopoverContent>
                                </Popover>
                            </div>
                        </div>
                        {selectedLoras.length > 0 && selectedLoras[0].model === "Flux.Pro" && <div>
                                    <p className="text-xs text-yellow-400">
                                        Cost: +1 gold per image when using Loras
                                        with Flux Pro Models
                                    </p>
                                </div>}
                        <div className="flex flex-wrap items-center gap-3">
                            {selectedLoras.map(lora => <Badge key={lora.id} variant="secondary" className="px-3 py-1 text-sm !rounded-full !bg-accent !text-white flex items-center gap-2 transition-all">
                                    {lora.loraName} (
                                    {(lora.weight ?? 1).toFixed(2)})
                                    <Button variant="ghost" size="sm" className="h-5 w-5 p-0 !rounded-full !bg-transparent text-white hover:text-white" onClick={() => handleDeselectLora(lora.id)}>
                                        <X className="h-3 w-3" />
                                        <span className="sr-only">
                                            Remove {lora.loraName}
                                        </span>
                                    </Button>
                                </Badge>)}
                        </div>
                    </> : <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div style={{
        display: "flex",
        alignItems: "center",
        gap: "0.5rem"
      }}>
                            <Switch id="disableArtStyleLora" checked={formValues["Disable Art Style"] === "true"} onCheckedChange={checked => handleArtStyleChange("Disable Art Style", checked.toString())} className="" />
                            <Span>Disable Art Style Lora</Span>
                            <Popover>
                                <PopoverTrigger asChild>
                                    <Button variant="ghost" className="text-gray-300 hover:text-white !px-0 !py-0 hover:bg-gray-800 !rounded-full transition-all duration-200 md:!flex-initial" aria-label="More options">
                                        <HelpCircle className="h-4 w-4 text-[#7AA8E3] cursor-help" />
                                    </Button>
                                </PopoverTrigger>
                                <PopoverContent className="!rounded-xl border !border-slate-200/20 !bg-slate-950 p-4">
                                    <p className="mb-2">
                                        Art Styles include specific prompt
                                        wording and a custom-trained Lora to
                                        recreate the style.
                                    </p>
                                    <p className="mb-2">
                                        Toggle this option to:
                                    </p>
                                    <ul className="list-disc pl-4">
                                        <li>
                                            Disable the Art Style Lora (Not your
                                            selected Loras)
                                        </li>
                                        <li>
                                            And Retain the Art Style Prompt
                                            Wording
                                        </li>
                                        <li>
                                            Avoid conflicts between your
                                            selected Loras and the Art Style.
                                        </li>
                                    </ul>
                                </PopoverContent>
                            </Popover>
                        </div>
                    </div>}

                {modelSupportsImageToImage(selectedModel) && renderReferenceUpload()}

                {modelSupportsControlNet(selectedModel) && renderSketchTool()}

                <Accordion type="multiple" data-sentry-element="Accordion" data-sentry-source-file="ImageForm.tsx">
                    {category !== "characters" && <AccordionItem value="category-specific">
                            <AccordionTrigger className="text-white">
                                Category-Specific Fields
                            </AccordionTrigger>
                            <AccordionContent>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {dynamicFields && Object.entries(dynamicFields?.npcFieldsMap).map(([field, options]) => renderTransformableField(field, options))}
                                    {category === "landscapes" && <>
                                            <div className="w-full grid grid-cols-1 gap-4 mb-4 flex flex-col">
                                                <Label htmlFor="landscapeName">
                                                    Landscape Name
                                                </Label>
                                                <Input className="!bg-primary !text-white border !border-slate-600/50 p-2 !rounded-md !w-full focus:!border-sky-600" id="landscapeName" value={formValues["Landscape Name"] || ""} onChange={handleInputChange("Landscape Name")} placeholder="Enter Landscape Name" />
                                            </div>
                                            <div className="w-full grid grid-cols-1 gap-4 mb-4 flex flex-col">
                                                <Label htmlFor="landscapePrompt">
                                                    Landscape Prompt
                                                </Label>
                                                <Textarea className="w-full h-24 !bg-primary text-white !border !border-slate-600/50 rounded-md p-2" id="landscapePrompt" value={theme?.fullName} onChange={handleInputThemeChange} placeholder='e.g., "a sunlit forest clearing with wildflowers, a beach at sunset with pink and orange skies, snowy mountains under northern lights"' />
                                            </div>
                                        </>}

                                    {category === "battlemaps" && <>
                                            <div className="w-full grid grid-cols-1 gap-4 mb-4 flex flex-col">
                                                <Label htmlFor="battlemapName">
                                                    Battlemap Name
                                                </Label>
                                                <Input className="!bg-primary !text-white border !border-slate-600/50 p-2 !rounded-md !w-full focus:!border-sky-600" id="battlemapName" value={formValues["Battlemap Name"] || ""} onChange={handleInputChange("Battlemap Name")} placeholder="Enter Battlemap Name" />
                                            </div>
                                            <div className="w-full grid grid-cols-1 gap-4 mb-4 flex flex-col">
                                                <Label htmlFor="battlemapPrompt">
                                                    Battlemap Prompt
                                                </Label>
                                                <Textarea className="w-full h-24 !bg-primary text-white !border !border-slate-600/50 rounded-md p-2" id="battlemapPrompt" value={theme?.fullName} onChange={handleInputThemeChange} placeholder="Enter prompt for your battlemap. It's a good idea to include any rooms, top down, battlemap in your prompt." />
                                            </div>
                                        </>}

                                    {category === "items" && <>
                                            <div className="w-auto gap-4 mb-4 flex flex-col">
                                                <Label htmlFor="itemName">
                                                    Item Name
                                                </Label>
                                                <Input className="!bg-primary !text-white border !border-slate-600/50 p-2 !rounded-md !w-full focus:!border-sky-600" id="itemName" value={formValues["Item Name"] || ""} onChange={handleInputChange("Item Name")} placeholder="Enter Item Name" />
                                            </div>
                                            <div className="w-full grid grid-cols-1 gap-4 mb-4 flex flex-col">
                                                <div className="flex flex-row gap-4">
                                                    <Label htmlFor="itemPrompt">
                                                        Item Prompt
                                                    </Label>
                                                    <Popover>
                                                        <PopoverTrigger asChild>
                                                            <Button variant="ghost" className="text-gray-300 hover:text-white !px-0 !py-0 hover:bg-gray-800 !rounded-full transition-all duration-200 md:!flex-initial" aria-label="More options">
                                                                <HelpCircle className="h-4 w-4 text-[#7AA8E3] cursor-help" />
                                                            </Button>
                                                        </PopoverTrigger>
                                                        <PopoverContent className="!rounded-xl border !border-slate-200/20 !bg-slate-950 p-4">
                                                            <p>
                                                                Specify the
                                                                objects,
                                                                accessories, or
                                                                equipment you
                                                                want to include
                                                                in your image
                                                            </p>
                                                            <p>
                                                                Clearly describe
                                                                each item to
                                                                ensure accurate
                                                                representation.{" "}
                                                            </p>
                                                            <p>For example:</p>
                                                            <ul className="list-disc pl-4">
                                                                <li>
                                                                    Weapon:
                                                                    &apos;a
                                                                    gleaming
                                                                    longsword
                                                                    with
                                                                    intricate
                                                                    engravings&apos;
                                                                </li>
                                                                <li>
                                                                    Accessory:
                                                                    &apos;a
                                                                    jeweled
                                                                    necklace
                                                                    with emerald
                                                                    stones&apos;
                                                                </li>
                                                                <li>
                                                                    Equipment:
                                                                    &apos;a
                                                                    sturdy
                                                                    backpack
                                                                    with
                                                                    multiple
                                                                    compartments&apos;
                                                                </li>
                                                                <li>
                                                                    Magic Items:
                                                                    &apos;a
                                                                    floating
                                                                    crystal orb
                                                                    emitting a
                                                                    soft blue
                                                                    glow&apos;
                                                                </li>
                                                            </ul>
                                                        </PopoverContent>
                                                    </Popover>
                                                </div>
                                                <Textarea className="w-full h-24 !bg-primary text-white !border !border-slate-600/50 rounded-md p-2" id="itemPrompt" value={theme?.fullName} onChange={handleInputThemeChange} placeholder='e.g., "a gleaming longsword with intricate engravings, a sturdy shield adorned with a lion emblem, and a magical amulet glowing with blue energy"' />
                                            </div>
                                        </>}
                                </div>
                            </AccordionContent>
                        </AccordionItem>}

                    {category === "characters" && <AccordionItem value="character-specific">
                            <AccordionTrigger className="text-white" id="character-specific">
                                Character Reference (Required)
                            </AccordionTrigger>
                            <AccordionContent>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <div className="flex items-center space-x-2 mb-2">
                                            <Label htmlFor="file-upload" className="joyride-character-sheet-file">
                                                File Upload
                                            </Label>
                                            <HelpCircle className="h-4 w-4 text-[#7AA8E3] cursor-help" onClick={e => {
                  e.preventDefault();
                  e.stopPropagation();
                  setHowToModalOpen(true);
                }} />
                                        </div>
                                        <div style={{
                display: "flex",
                alignItems: "center",
                gap: "0.5rem"
              }}>
                                            <Input className="!bg-primary !text-white border !border-slate-600/50 p-2 !rounded-md !w-full focus:!border-sky-600" id="file-upload" type="file" accept=".pdf" onChange={handleFileChange} />
                                            <Button type="button" size="icon" onClick={() => document.getElementById("file-upload")?.click()}>
                                                <Upload />
                                            </Button>
                                        </div>
                                    </div>
                                    <div>
                                        <Label htmlFor="url-input" className="joyride-character-sheet-url">
                                            URL Input
                                        </Label>
                                        <Input className="!bg-primary !text-white border !border-slate-600/50 p-2 !rounded-md !w-full focus:!border-sky-600" id="url-input" type="url" value={url} onChange={handleUrlChange} placeholder="Enter URL" />
                                    </div>
                                </div>
                            </AccordionContent>
                        </AccordionItem>}

                    <AccordionItem key="additional-info" value="additional-info" data-sentry-element="AccordionItem" data-sentry-source-file="ImageForm.tsx">
                        <AccordionTrigger className="text-white joyride-additional-info" data-sentry-element="AccordionTrigger" data-sentry-source-file="ImageForm.tsx">
                            Additional Information
                        </AccordionTrigger>
                        <AccordionContent data-sentry-element="AccordionContent" data-sentry-source-file="ImageForm.tsx">
                            <div style={{
            display: "flex",
            flexDirection: "column",
            gap: "1rem"
          }}>
                                {(category === "characters" || category === "npcs" || category === "monsters") && <div>
                                        <div className="flex flex-row gap-4">
                                            <Label htmlFor="prompt">
                                                Additional Information:
                                            </Label>
                                            <Popover>
                                                <PopoverTrigger asChild>
                                                    <Button variant="ghost" className="text-gray-300 hover:text-white !px-0 !py-0 hover:bg-gray-800 !rounded-full transition-all duration-200 md:!flex-initial" aria-label="More options">
                                                        <HelpCircle className="h-4 w-4 text-[#7AA8E3] cursor-help" />
                                                    </Button>
                                                </PopoverTrigger>
                                                <PopoverContent className="!rounded-xl border !border-slate-200/20 !bg-slate-950 p-4">
                                                    <div>
                                                        <p>
                                                            Add any extra
                                                            details to further
                                                            customize your
                                                            image.
                                                        </p>
                                                        <p>
                                                            Specify poses,
                                                            mounts, clothing,
                                                            equipment, and other
                                                            specific elements to
                                                            ensure the generated
                                                            artwork aligns
                                                            perfectly with your
                                                            vision.
                                                        </p>
                                                        <p>For example:</p>
                                                        <ul className="list-disc pl-4">
                                                            <li>
                                                                Poses:
                                                                &apos;standing
                                                                with arms
                                                                crossed&apos;
                                                            </li>
                                                            <li>
                                                                Mounts:
                                                                &apos;riding a
                                                                majestic
                                                                dragon&apos;
                                                            </li>
                                                            <li>
                                                                Clothing:
                                                                &apos;wearing a
                                                                flowing red
                                                                cloak&apos;
                                                            </li>
                                                            <li>
                                                                Equipment:
                                                                &apos;holding a
                                                                gleaming sword
                                                                and shield&apos;
                                                            </li>
                                                        </ul>
                                                    </div>
                                                </PopoverContent>
                                            </Popover>
                                        </div>
                                        <Textarea className="w-full h-24 !bg-primary text-white !border !border-slate-600/50 rounded-md p-2" id="description" value={formValues["Description"] || ""} onChange={handleInputChange("Description")} placeholder="Add any extra details to further customize your image. Specify poses, mounts, clothing, equipment, and other specific elements to ensure the generated artwork aligns perfectly with your vision." />
                                    </div>}
                                {selectedModel?.hasNegativePrompt && <div>
                                        <Label htmlFor="negativePrompt" className="negativePrompt">
                                            Negative Prompt (Optional)
                                        </Label>
                                        <Textarea className="w-full h-24 !bg-primary text-white !border !border-slate-600/50 rounded-md p-2" id="negativePrompt" value={formValues["Negative Prompt"] || ""} onChange={handleInputChange("Negative Prompt")} placeholder={"Negative Prompts allow you to specify what you don't want to see in the image. Use keywords separated by commas. Eg: 'ugly, anime, cartoon, male.' Keep it simple and to the point!"} />
                                    </div>}
                            </div>
                        </AccordionContent>
                    </AccordionItem>
                </Accordion>
            </div>;
  const renderSimpleForm = () => <div style={{
    display: "flex",
    flexDirection: "column",
    gap: "1rem"
  }} data-sentry-component="renderSimpleForm" data-sentry-source-file="ImageForm.tsx">
                {renderCommonFields()}

                {selectedModel && modelsWithLoras.includes(selectedModel.modelName) && !isPremium ? <>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-center">
                            <LoraSelectionModal loras={filteredLoras} onLoraSelected={onLoraSelected} selectedLoras={selectedLoras} handleToggleChange={toggleLora} />
                            <div style={{
          display: "flex",
          alignItems: "center",
          gap: "0.5rem"
        }}>
                                <Switch id="disableArtStyleLora" checked={formValues["Disable Art Style"] === "true"} onCheckedChange={checked => handleArtStyleChange("Disable Art Style", checked.toString())} className="" />
                                <Span>Disable Art Style Lora</Span>
                                <Popover>
                                    <PopoverTrigger asChild>
                                        <Button variant="ghost" className="text-gray-300 hover:text-white !px-0 !py-0 hover:bg-gray-800 !rounded-full transition-all duration-200 md:!flex-initial" aria-label="More options">
                                            <HelpCircle className="h-4 w-4 text-[#7AA8E3] cursor-help" />
                                        </Button>
                                    </PopoverTrigger>
                                    <PopoverContent className="!rounded-xl border !border-slate-200/20 !bg-slate-950 p-4">
                                        <p className="mb-2">
                                            Art Styles include specific prompt
                                            wording and a custom-trained Lora to
                                            recreate the style.
                                        </p>
                                        <p className="mb-2">
                                            Toggle this option to:
                                        </p>
                                        <ul className="list-disc pl-4">
                                            <li>
                                                Disable the Art Style Lora (Not
                                                your selected Loras)
                                            </li>
                                            <li>
                                                And Retain the Art Style Prompt
                                                Wording
                                            </li>
                                            <li>
                                                Avoid conflicts between your
                                                selected Loras and the Art
                                                Style.
                                            </li>
                                        </ul>
                                    </PopoverContent>
                                </Popover>
                            </div>
                        </div>
                        {selectedLoras.length > 0 && selectedLoras[0].model === "Flux.Pro" && <div>
                                    <p className="text-xs text-yellow-400">
                                        Cost: +1 gold per image when using Loras
                                        with Flux Pro Models
                                    </p>
                                </div>}
                        <div className="flex flex-wrap items-center gap-3">
                            {selectedLoras.map(lora => <Badge key={lora.id} variant="secondary" className="px-3 py-1 text-sm !rounded-full !bg-accent !text-white flex items-center gap-2 transition-all">
                                    {lora.loraName} (
                                    {(lora.weight ?? 1).toFixed(2)})
                                    <Button variant="ghost" size="sm" className="h-5 w-5 p-0 !rounded-full !bg-transparent text-white hover:text-white" onClick={() => handleDeselectLora(lora.id)}>
                                        <X className="h-3 w-3" />
                                        <span className="sr-only">
                                            Remove {lora.loraName}
                                        </span>
                                    </Button>
                                </Badge>)}
                        </div>
                    </> : <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-center">
                        <div style={{
        display: "flex",
        alignItems: "center",
        gap: "0.5rem"
      }}>
                            <Switch id="disableArtStyleLora" checked={formValues["Disable Art Style"] === "true"} onCheckedChange={checked => handleArtStyleChange("Disable Art Style", checked.toString())} className="" />
                            <Span>Disable Art Style Lora</Span>
                            <Popover>
                                <PopoverTrigger asChild>
                                    <Button variant="ghost" className="text-gray-300 hover:text-white !px-0 !py-0 hover:bg-gray-800 !rounded-full transition-all duration-200 md:!flex-initial" aria-label="More options">
                                        <HelpCircle className="h-4 w-4 text-[#7AA8E3] cursor-help" />
                                    </Button>
                                </PopoverTrigger>
                                <PopoverContent className="!rounded-xl border !border-slate-200/20 !bg-slate-950 p-4">
                                    <p className="mb-2">
                                        Art Styles include specific prompt
                                        wording and a custom-trained Lora to
                                        recreate the style.
                                    </p>
                                    <p className="mb-2">
                                        Toggle this option to:
                                    </p>
                                    <ul className="list-disc pl-4">
                                        <li>
                                            Disable the Art Style Lora (Not your
                                            selected Loras)
                                        </li>
                                        <li>
                                            And Retain the Art Style Prompt
                                            Wording
                                        </li>
                                        <li>
                                            Avoid conflicts between your
                                            selected Loras and the Art Style.
                                        </li>
                                    </ul>
                                </PopoverContent>
                            </Popover>
                        </div>
                    </div>}

                <div>
                    {selectedModel?.modelName === "Flux.Pro" || selectedModel?.modelName === "Ideogram 2.0" || selectedModel?.modelName === "Flux.Pro (Ultra)" || isPremium || selectedModel?.modelName === "Flux.Dev" || selectedModel?.modelName === "Flux.Schnell" || selectedModel?.modelName === "AuraFlow" || selectedModel?.modelName === "Recraft V3" || selectedModel?.modelName === "Playground V2.5" ? <>
                            <div className="flex flex-row gap-4">
                                <Label htmlFor="prompt">Prompt</Label>
                                <Popover>
                                    <PopoverTrigger asChild>
                                        <Button variant="ghost" className="text-gray-300 hover:text-white !px-0 !py-0 hover:bg-gray-800 !rounded-full transition-all duration-200 md:!flex-initial" aria-label="More options">
                                            <HelpCircle className="h-4 w-4 text-[#7AA8E3] cursor-help" />
                                        </Button>
                                    </PopoverTrigger>
                                    <PopoverContent className="!rounded-xl border !border-slate-200/20 !bg-slate-950 p-4">
                                        <p>
                                            Use natural language to describe
                                            what you want in the image.
                                        </p>
                                        <br />
                                        <p>
                                            Mention details like appearance,
                                            setting, and mood.
                                        </p>
                                        <br />
                                        <p>
                                            For example: &apos;A fierce dragon
                                            soaring over a mountain range at
                                            sunset, scales glinting in the
                                            golden light.&apos;
                                        </p>
                                        <br />
                                        <p>
                                            The more specific you are, the
                                            better the result.
                                        </p>
                                    </PopoverContent>
                                </Popover>
                            </div>
                            <Textarea className="w-full h-24 !bg-primary text-white !border !border-slate-600/50 rounded-md p-2" id="simplePrompt" value={prompt} onChange={e => setPrompt(e.target.value)} placeholder={"Use natural language to describe what you want in the image. Mention details like appearance, setting, and mood. For example: 'A fierce dragon soaring over a mountain range at sunset, scales glinting in the golden light.' The more specific you are, the better the result."} />
                        </> : <>
                            <div className="flex flex-row gap-4">
                                <Label htmlFor="prompt">Prompt</Label>
                            </div>

                            <Popover>
                                <PopoverTrigger asChild>
                                    <Button variant="ghost" className="text-gray-300 hover:text-white !px-0 !py-0 hover:bg-gray-800 !rounded-full transition-all duration-200 md:!flex-initial" aria-label="More options">
                                        <HelpCircle className="h-4 w-4 text-[#7AA8E3] cursor-help" />
                                    </Button>
                                </PopoverTrigger>
                                <PopoverContent className="!rounded-xl border !border-slate-200/20 !bg-slate-950 p-4">
                                    <p>
                                        Use key terms with optional weights to
                                        focus on important aspects.
                                    </p>
                                    <br />
                                    <p>
                                        For example, &apos;dragon:2, mountain,
                                        sunset:1.5&apos;
                                    </p>
                                    <br />
                                    <p>
                                        gives extra emphasis to
                                        &apos;dragon&apos; and
                                        &apos;sunset&apos; without full
                                        sentences.
                                    </p>
                                    <br />
                                    <p>
                                        Higher weights (like 2 or 1.5) make
                                        keywords more prominent in the final
                                        image
                                    </p>
                                </PopoverContent>
                            </Popover>
                            <Textarea className="w-full h-24 !bg-primary text-white !border !border-slate-600/50 rounded-md p-2" id="simplePrompt" value={prompt} onChange={e => setPrompt(e.target.value)} placeholder={"Use key terms with optional weights to focus on important aspects. For example, 'dragon:2, mountain, sunset:1.5' gives extra emphasis to 'dragon' and 'sunset' without full sentences. Higher weights (like 2 or 1.5) make keywords more prominent in the final image"} />
                        </>}
                </div>

                <div style={{
      display: "flex",
      alignItems: "center",
      gap: "0.5rem"
    }}>
                    <Switch id="promptEnhance" checked={formValues["Enhance Prompt"] === "true"} onCheckedChange={checked => handleArtStyleChange("Enhance Prompt", checked.toString())} data-sentry-element="Switch" data-sentry-source-file="ImageForm.tsx" />
                    <div className="flex items-center space-x-2">
                        <Span data-sentry-element="Span" data-sentry-source-file="ImageForm.tsx">Enhance Prompt</Span>

                        <Popover data-sentry-element="Popover" data-sentry-source-file="ImageForm.tsx">
                            <PopoverTrigger asChild data-sentry-element="PopoverTrigger" data-sentry-source-file="ImageForm.tsx">
                                <Button variant="ghost" className="text-gray-300 hover:text-white !px-0 !py-0 hover:bg-gray-800 !rounded-full transition-all duration-200 md:!flex-initial" aria-label="More options" data-sentry-element="Button" data-sentry-source-file="ImageForm.tsx">
                                    <HelpCircle className="h-4 w-4 text-[#7AA8E3] cursor-help" data-sentry-element="HelpCircle" data-sentry-source-file="ImageForm.tsx" />
                                </Button>
                            </PopoverTrigger>
                            <PopoverContent className="!rounded-xl border !border-slate-200/20 !bg-slate-950 p-4" data-sentry-element="PopoverContent" data-sentry-source-file="ImageForm.tsx">
                                <p>
                                    Enabling this option will let CharGen
                                    automatically enhance your prompt with AI to
                                    improve the quality of the generated image.
                                </p>
                                <br />
                                <p>
                                    Instead of having to write super long
                                    complicated prompts, you can use Prompt
                                    Enhancer with simple prompts and let
                                    CharGens AI creativity do the rest!
                                </p>
                            </PopoverContent>
                        </Popover>
                    </div>
                </div>
                {modelSupportsImageToImage(selectedModel) && renderReferenceUpload()}

                {modelSupportsControlNet(selectedModel) && renderSketchTool()}

                <Collapsible data-sentry-element="Collapsible" data-sentry-source-file="ImageForm.tsx">
                    <CollapsibleTrigger className={`flex items-center justify-between w-full p-2 bg-gradient-to-r from-[${colourScheme.primaryLight}] to-[${colourScheme.primary}] text-[${colourScheme.white}] rounded-md hover:bg-gradient-to-r hover:from-[${colourScheme.primary}] hover:to-[${colourScheme.primaryLight}]`} data-sentry-element="CollapsibleTrigger" data-sentry-source-file="ImageForm.tsx">
                        <Span data-sentry-element="Span" data-sentry-source-file="ImageForm.tsx">Optional Fields</Span>
                        <ChevronDown data-sentry-element="ChevronDown" data-sentry-source-file="ImageForm.tsx" />
                    </CollapsibleTrigger>
                    <CollapsibleContent style={{
        marginTop: "1rem",
        display: "flex",
        flexDirection: "column",
        gap: "1rem"
      }} data-sentry-element="CollapsibleContent" data-sentry-source-file="ImageForm.tsx">
                        {selectedModel?.hasNegativePrompt && <div>
                                <Label htmlFor="negativePrompt" className="negativePrompt">
                                    Negative Prompt (Optional)
                                </Label>
                                <Textarea id="negativePrompt" value={formValues["Negative Prompt"] || ""} onChange={handleInputChange("Negative Prompt")} placeholder={"Negative Prompts allow you to specify what you don't want to see in the image. Use keywords separated by commas. Eg: 'ugly, anime, cartoon, male.' Keep it simple and to the point!"} className="w-full h-24 !bg-primary text-white !border !border-slate-600/50 rounded-md p-2" />
                            </div>}

                        {renderTransformableField("Art Style", [])}
                        <div style={{
          display: "flex",
          alignItems: "center",
          gap: "0.5rem"
        }}>
                            <Switch id="useArtStyle" checked={includeArtStyle} onCheckedChange={checked => setIncludeArtStyle(checked)} data-sentry-element="Switch" data-sentry-source-file="ImageForm.tsx" />
                            <Span data-sentry-element="Span" data-sentry-source-file="ImageForm.tsx">Use Art Style (Optional)</Span>
                        </div>
                    </CollapsibleContent>
                </Collapsible>
            </div>;
  return loadingArtStyles || loadingModels || loadingLoras || loadingPresets || loadingThemes ? <SkeletonForm /> : <>
                <CustomJoyride steps={imageFormSteps} run={run} stepIndex={stepIndex} disableScrolling={false} callback={handleJoyrideCallback} />
                <CustomJoyride steps={promptingTutorialSteps} run={runPromptTutorial} stepIndex={promptStepIndex} callback={handlePromptJoyrideCallback} disableScrolling={false} />
                <CardContent className="">
                    <form onSubmit={handleSubmit} className="flex flex-col gap-2">
                        <div className="flex flex-col gap-4">
                            <Tabs className="!w-full" value={formType} onValueChange={setFormType}>
                                <TabsList className="w-full gap-1 flex flex-col md:flex-row !rounded-lg !p-0 !bg-transparent mb-6 mt-4 items-center justify-center">
                                    {["simple", "detailed"].map(mode => <TabsTrigger key={mode} value={mode} onClick={e => {
                e.preventDefault();
                e.stopPropagation();
                setFormType(mode);
              }} className={`w-full flex !items-center !justify-center !rounded-lg text-center transition-all duration-200 !p-1 ${formType === mode ? `font-semibold bg-gradient-to-r from-[${colourScheme.secondary}] to-[${colourScheme.secondaryLight}] text-[${colourScheme.fullWhite}] hover:bg-[${colourScheme.secondaryLight}] hover:!text-[${colourScheme.white}]` : `font-normal bg-[${colourScheme.primaryLight}] text-[${colourScheme.grey}] hover:bg-[${colourScheme.primaryDark}] hover:text-[${colourScheme.white}]`}`}>
                                            <div className="flex items-center justify-center gap-2">
                                                {mode === "simple" ? <Zap size={16} /> : <Settings size={16} />}
                                                {mode === "simple" ? "Quick Generate" : "Custom Generate"}
                                            </div>
                                        </TabsTrigger>)}
                                </TabsList>
                                {renderCategorySelection()}
                                <TabsContent value="simple">
                                    {formType === "simple" && renderSimpleForm()}
                                </TabsContent>
                                <TabsContent value="detailed">
                                    {formType === "detailed" && renderDetailedForm()}
                                </TabsContent>
                            </Tabs>
                            <CardFooter className="flex flex-col gap-4 items-end w-full">
                                {!isSubscribed && <div className="flex items-center gap-2">
                                        <Switch id="removeWatermark" checked={removeWatermark} onCheckedChange={checked => setRemoveWatermark(checked)} />
                                        <span className="text-sm text-slate-300">
                                            Remove Watermark (1 Gold/Image)
                                        </span>
                                        <Popover>
                                            <PopoverTrigger asChild>
                                                <Button variant="ghost" className="text-slate-300 hover:text-white !p-2" aria-label="Info: Watermark removal">
                                                    <HelpCircle className="h-4 w-4" />
                                                </Button>
                                            </PopoverTrigger>
                                            <PopoverContent className="p-4 bg-slate-800 rounded-md">
                                                <p className="text-sm text-slate-300">
                                                    Subscribe to Plus, Elite, or
                                                    Ultimate tier for free
                                                    watermark removal, or enable
                                                    this option to pay 1 gold
                                                    per image.
                                                </p>
                                                <div className="mt-4 flex">
                                                    <Button variant="link" className="text-sm !bg-emerald-500/50 text-slate-300 hover:text-white p-0 h-auto" onClick={() => {
                      if (state.userPremiumLevel === UserPremiumType.NONE) {
                        dispatch({
                          type: AppReducerActionType.SHOW_SUBSCRIPTION_TABLE,
                          payload: {
                            ...state,
                            showSubscriptionTable: true
                          }
                        });
                      } else {
                        window.open(`${STRIPE_CUSTOMER_PORTAL}?prefilled_email=${state.email}`, "_blank");
                      }
                    }}>
                                                        Manage Subscription
                                                    </Button>
                                                </div>
                                            </PopoverContent>
                                        </Popover>
                                    </div>}
                                <div className="flex flex-col gap-4 p-0 md:flex-row md:items-center md:justify-between w-full">
                                    <div className="flex flex-col gap-4 md:flex-row">
                                        <Button type="button" className={`joyride-save-preset
                      !bg-[${colourScheme.primaryLight}]
                      text-white border border-[${colourScheme.primaryLight}]
                      transition duration-300 ease-in-out
                      hover:brightness-110 w-full md:w-auto`} onClick={() => setIsSavePresetModalOpen(true)}>
                                            <Save style={{
                    marginRight: "0.5rem"
                  }} />
                                            Save Preset
                                        </Button>
                                    </div>
                                    <div className="flex flex-col gap-4 items-start w-full md:w-auto md:flex-row md:items-center md:justify-end">
                                        <div className="joyride-total-cost flex items-center gap-2 md:gap-4">
                                            <Coins className="h-5 w-5 text-[#F0B853]" />
                                            <span>
                                                Total Gold Cost: {totalGoldCost}
                                            </span>
                                        </div>
                                        <Button type="submit" className="joyride-generate-button w-full md:w-auto" disabled={loading}>
                                            <Wand2 className="mr-2 h-4 w-4" />
                                            Generate Image
                                        </Button>
                                    </div>
                                </div>
                            </CardFooter>
                        </div>
                    </form>
                </CardContent>
                <QuestionModal isOpen={isHowToModalOpen} handleBackdropClick={() => setHowToModalOpen(false)} />
                <SavePresetModal isOpen={isSavePresetModalOpen} onCancel={() => setIsSavePresetModalOpen(false)} presets={presets} onSave={handleSave} />
            </>;
});
ImageForm.displayName = "ImageForm";
export default ImageForm;